import isDescendantElement from '../helpers/isDescendantElement';
import inBetween from '../helpers/inBetween';
import classify from '../helpers/classify';
import getPressedKey, { KeyboardMap } from '../helpers/getPressedKey';

function listbox(
  listboxElement: Element | null,
  options?: {
    classNames?: {
      active: string;
      button: string;
      list: string;
    };
    clickCallback?: (x: Event) => void;
    toggleCallback?: (x: boolean) => void;
  }
): { initListbox: () => void; destroyListbox: () => void } {
  const LISTBOX_ACTIVE_CLASS = options?.classNames?.active || 'listbox--active';
  const LISTBOX_BUTTON_CLASS = options?.classNames?.button || 'listbox__button';
  const LISTBOX_LIST_CLASS = options?.classNames?.list || 'listbox__list';

  const listboxTrigger = listboxElement?.querySelector(classify(LISTBOX_BUTTON_CLASS));
  const listboxOptionsList = listboxElement?.querySelector(classify(LISTBOX_LIST_CLASS));
  const listboxOptions = Array.from(listboxElement?.querySelectorAll('[role="option"]') || []);

  let isListboxOpen = false;
  let activeDescendant: Element | null = null;

  if (
    listboxElement === null ||
    listboxTrigger === null ||
    listboxOptionsList === null ||
    listboxOptions.length === 0
  ) {
    return { initListbox: (): void => {}, destroyListbox: (): void => {} };
  }
  return { initListbox, destroyListbox };

  function showListbox(): void {
    /* istanbul ignore else */
    if (listboxElement?.classList.contains(LISTBOX_ACTIVE_CLASS)) {
      return;
    }
    isListboxOpen = true;
    if (options?.toggleCallback) {
      options.toggleCallback(isListboxOpen);
    }
    listboxElement?.classList.add(LISTBOX_ACTIVE_CLASS);
    (listboxTrigger as HTMLButtonElement).setAttribute('aria-expanded', 'true');
    document.addEventListener('keydown', keydownHandler);
    (listboxOptionsList as HTMLUListElement).focus();
  }

  function hideListbox(e?: Event): void {
    if (!listboxElement?.classList.contains(LISTBOX_ACTIVE_CLASS)) {
      return;
    }

    function hideListboxForReal(): void {
      isListboxOpen = false;
      if (options?.toggleCallback) {
        options?.toggleCallback(isListboxOpen);
      }
      listboxElement?.classList.remove(LISTBOX_ACTIVE_CLASS);
      (listboxTrigger as HTMLButtonElement).setAttribute('aria-expanded', 'false');
      document.removeEventListener('keydown', keydownHandler);
      clearActiveDescendant();
    }

    if (e && e.type === 'focusout') {
      const parent = e.currentTarget || null;
      setTimeout(() => {
        const nextFocusedElement = document.activeElement;
        if (parent && nextFocusedElement && isDescendantElement(parent as HTMLElement, nextFocusedElement)) {
          return;
        }
        hideListboxForReal();
      }, 1);
      return;
    }

    hideListboxForReal();
  }

  function focusFirstOptionElement(): void {
    [activeDescendant] = listboxOptions;
    focusElement(activeDescendant);
  }

  function focusLastOptionElement(): void {
    activeDescendant = listboxOptions[listboxOptions.length - 1];
    focusElement(activeDescendant);
  }

  function focusElement(activeElement: Element): void {
    listboxElement?.setAttribute('aria-activedescendant', `${activeElement.id}`);
    activeElement.setAttribute('aria-selected', 'true');
    const focusableElement = activeElement.querySelector('a') || activeElement.querySelector('button');
    (focusableElement || (activeElement as HTMLLIElement)).focus();
    if (options && options.clickCallback) {
      activeElement.addEventListener('click', options.clickCallback);
    }
  }

  function clearActiveDescendant(): void {
    listboxElement?.removeAttribute('aria-activedescendant');
    if (activeDescendant) {
      if (options && options.clickCallback) {
        activeDescendant.removeEventListener('click', options.clickCallback);
      }
      activeDescendant.removeAttribute('aria-selected');
      activeDescendant = null;
    }
  }

  function navigateTo(key: string): void {
    const MIN_POSITION = 0;
    const MAX_POSITION = listboxOptions.length - 1;

    let activePosition = -1;
    if (activeDescendant) {
      activeDescendant.removeAttribute('aria-selected');
      activePosition = listboxOptions.indexOf(activeDescendant);
    }

    const directionMap: { [key: string]: number } = {
      [KeyboardMap.ARROW_UP]: activePosition - 1,
      [KeyboardMap.ARROW_DOWN]: activePosition + 1
    };

    const position = inBetween(MIN_POSITION, MAX_POSITION, directionMap[key]);

    activeDescendant = listboxOptions[position];
    focusElement(activeDescendant);
  }

  function keydownHandler(e: KeyboardEvent): void {
    const keydownHandlerMap: {
      [key: string]: () => void; // [key in KeyboardMap]: () => void;
    } = {
      [KeyboardMap.ESCAPE](): void {
        e.preventDefault();
        hideListbox();
      },
      [KeyboardMap.ARROW_UP](): void {
        e.preventDefault();
        navigateTo(KeyboardMap.ARROW_UP);
      },
      [KeyboardMap.ARROW_DOWN](): void {
        e.preventDefault();
        navigateTo(KeyboardMap.ARROW_DOWN);
      },
      [KeyboardMap.HOME](): void {
        e.preventDefault();
        focusFirstOptionElement();
      },
      [KeyboardMap.END](): void {
        e.preventDefault();
        focusLastOptionElement();
      }
    };
    /* istanbul ignore else */
    if (keydownHandlerMap[getPressedKey(e)]) {
      keydownHandlerMap[getPressedKey(e)]();
    }
  }

  function triggerKeyupHandler(e: Event): void {
    const keyupHandlerMap: {
      [key: string]: () => void; // [key in KeyboardMap]: () => void;
    } = {
      [KeyboardMap.ARROW_UP](): void {
        e.preventDefault();
        showListbox();
        focusLastOptionElement();
      },
      [KeyboardMap.ARROW_DOWN](): void {
        e.preventDefault();
        showListbox();
        focusFirstOptionElement();
      }
    };
    /* istanbul ignore else */
    if (keyupHandlerMap[getPressedKey(e as KeyboardEvent)]) {
      keyupHandlerMap[getPressedKey(e as KeyboardEvent)]();
    }
  }

  function clickHandler(): void {
    return isListboxOpen ? hideListbox() : showListbox();
  }

  function initListbox(): void {
    if (listboxElement && listboxTrigger) {
      listboxElement.addEventListener('focusout', hideListbox);
      listboxTrigger.addEventListener('click', clickHandler);
      listboxTrigger.addEventListener('keyup', triggerKeyupHandler);
    }
  }

  function destroyListbox(): void {
    if (listboxElement && listboxTrigger) {
      listboxElement.removeEventListener('focusout', hideListbox);
      listboxTrigger.removeEventListener('click', clickHandler);
      listboxTrigger.removeEventListener('keyup', triggerKeyupHandler);
    }
  }
}

export default listbox;
